'use client'

/**
 * Sign-in hook for the lbx-training SDK pathway.
 *
 * On every `signed_in` / `INITIAL_SESSION` event from the AuthGateway:
 *   1. Calls `fetchOrCreateSelfMember()` — idempotent RPC that returns
 *      the user's `(household_id, member_id)` self pair, bootstrapping
 *      household + self-row on first sign-in.
 *   2. Wires the pair into the TrainingCaptureCoordinator via
 *      `configureIdentity(...)` so the coordinator stops being a
 *      silent no-op for normal flows.
 *   3. Hydrates the consent-store snapshot from `circle_members`
 *      so the consent UI doesn't flicker on first paint after sign-in.
 *
 * On `signed_out`:
 *   - Clears the coordinator identity (so any in-flight
 *     `beginVoiceSession` call no-ops).
 *   - Resets the consent-store cache to fail-closed defaults so the
 *     next user who signs in doesn't briefly see the previous user's
 *     toggles.
 *
 * Failure-mode: the RPC failing is non-fatal for the rest of the
 * layout. The provider logs at warn level and leaves the coordinator
 * unconfigured; the consent UI shows its "sign in to manage consent"
 * empty state until the next event fires (e.g. token refresh).
 *
 * Mounted near the root in `src/app/layout.tsx` alongside the other
 * `<TelemetryConsumersProvider>` etc.
 */

import { useEffect } from 'react'
import { getAuthGateway } from '@/lib/auth/AuthGateway'
import { getTrainingCaptureCoordinator } from '@/lib/training/training-capture-coordinator'
import {
  hydrateTrainingConsentFromServer,
  resetTrainingConsentCache,
} from '@/lib/training/consent-store'
import { fetchOrCreateSelfMember } from '@/lib/training/self-member-service'
import { createLogger } from '@/lib/monitoring/logger'

const log = createLogger('training-identity-provider')

async function provisionSelfMember(): Promise<void> {
  try {
    const identity = await fetchOrCreateSelfMember()
    getTrainingCaptureCoordinator().configureIdentity(identity)
    await hydrateTrainingConsentFromServer(identity.memberId)
  } catch (err) {
    log.warn('self-member provision failed; coordinator stays unconfigured', {
      err: String(err),
    })
  }
}

export function TrainingIdentityProvider({
  children,
}: {
  children: React.ReactNode
}) {
  useEffect(() => {
    const gateway = getAuthGateway()

    // Kick off an immediate provision against the current session.
    // The AuthGateway emits `signed_in` for `INITIAL_SESSION` too,
    // but that path is async; calling here covers the case where
    // the layout mounts after a hot reload preserves the session.
    gateway
      .currentSnapshot()
      .then((snap) => {
        if (snap.isAuthenticated) {
          provisionSelfMember()
        }
      })
      .catch((err) => {
        log.warn('initial snapshot read failed', { err: String(err) })
      })

    const unsubscribe = gateway.subscribe((event) => {
      switch (event.kind) {
        case 'signed_in':
          provisionSelfMember()
          break
        case 'signed_out':
          getTrainingCaptureCoordinator().configureIdentity(null)
          resetTrainingConsentCache()
          break
        // token_refreshed / user_updated don't change identity; ignore.
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  return <>{children}</>
}
